import React, { useEffect, useMemo } from 'react';
import { Solicitud } from '@/types/solicitud';
import { User } from '@/types/user';
import { Empresa } from '@/types/empresa';
import { Switch } from '@/components/ui/Switch';
import { MontoInput } from '@/components/ui/MontoInput';
import { usePlazosDisponibles } from '@/hooks/usePlazosDisponibles';
import { SimuladorSIAC } from './SimuladorSIAC';
import { useAuth } from '@/hooks/useAuth';

interface AnalisisCreditosSectionProps {
  localSolicitud: Solicitud;
  setLocalSolicitud: React.Dispatch<React.SetStateAction<Solicitud | null>>;
  setHasChanges: (v: boolean) => void;
  error: string | null;
  asesores: User[];
  allUsers?: User[]; // Todos los usuarios (para Asesor Origen)
  empresas: Empresa[];
  loadingEmpresas?: boolean;
  onAsesorFinalChange?: (newAsesorId: string) => void;
  isRunningAnalisis: boolean;
  isSaving: boolean;
  handleRealizarAnalisisCreditos: () => Promise<void>;
  handleSimularSIAC: () => Promise<void>;
  handleSaveForm: () => Promise<void>;
  handleAnalisisConcluido: (checked: boolean) => Promise<void>;
  isTogglingConcluido: boolean;
  successMsg: string | null;
}

const fmt = (n: number) => `$${new Intl.NumberFormat('es-MX', { minimumFractionDigits: 2 }).format(n)}`;

const EXCEPCION_OPTIONS = [
  '', 'Avance de Clean Up', 'Menor a 1.7 Clean Up', 'Ratificación Domicilio',
  'Antigüedad', 'Buró', 'Aval', 'Buro del Aval', 'Tasa', 'Comisión',
  'Tasa y Comisión', 'Falta Documentación', 'Falta de Liquidez',
  'Mínimo Solicitado', 'Nivel de Riesgo', 'Es PEP', 'Firma Digital',
  'Monto excede políticas', 'Falta Convenio con Empresa', 'CleanUp Guadalajara',
];

export function AnalisisCreditosSection({
  localSolicitud,
  setLocalSolicitud,
  setHasChanges,
  error,
  asesores,
  allUsers,
  empresas,
  loadingEmpresas,
  onAsesorFinalChange,
  isRunningAnalisis,
  isSaving,
  handleRealizarAnalisisCreditos,
  handleSimularSIAC,
  handleSaveForm,
  handleAnalisisConcluido,
  isTogglingConcluido,
  successMsg,
}: AnalisisCreditosSectionProps) {
  const update = (field: string, value: unknown) => {
    setLocalSolicitud({ ...localSolicitud, [field]: value });
    setHasChanges(true);
  };

  // Auto-populate tipoEmpresa and nivelRiesgo from empresa data
  useEffect(() => {
    if (!localSolicitud.clienteEmpresa || empresas.length === 0) return;
    const emp = empresas.find(e => e.nombre === localSolicitud.clienteEmpresa);
    if (!emp) return;

    const tipoFromEmpresa = emp.tipo && (emp.tipo === 'Interna' || emp.tipo === 'Externa') ? emp.tipo : 'Externa';
    const nivelFromEmpresa = emp.nivel || 'Riesgo 1';

    if (localSolicitud.clienteTipoEmpresa !== tipoFromEmpresa || localSolicitud.clienteNivelRiesgoEmpresa !== nivelFromEmpresa) {
      setLocalSolicitud(prev => prev ? {
        ...prev,
        clienteTipoEmpresa: tipoFromEmpresa,
        clienteNivelRiesgoEmpresa: nivelFromEmpresa,
      } : prev);
    }
  }, [localSolicitud.clienteEmpresa, empresas]);

  // Validación Error por Montos - Cálculo síncrono con useMemo
  // Lógica replicada exactamente del legacy (routes_analisis.py)
  const calculatedErrorMontos = useMemo(() => {
    const mTrans = Number(localSolicitud.montoTransferencia || 0);
    const mAut = Number(localSolicitud.montoAutorizado || 0);
    const mSol = Number(localSolicitud.montoSolicitado || 0);
    const producto = localSolicitud.producto || '';
    const esCleanUp = localSolicitud.solicitudEsCleanUp === true;
    let errorMsg = '';

    if (producto === 'Adelanto de Nómina') {
      if (mTrans !== mAut) {
        errorMsg = 'Transferencia debe ser igual que Autorizado';
      }
    } else if (producto === 'Nómina') {
      if (esCleanUp) {
        if (mAut > mSol) {
          errorMsg = 'No se puede autorizar un monto mayor al solicitado.';
        }
        if (mTrans >= mAut) {
          errorMsg = 'Transferencia debe ser menor que Autorizado.';
        }
      } else {
        if (mTrans !== mAut) {
          errorMsg = 'Transferencia debe ser igual que Autorizado';
        }
        if (mTrans > mAut) {
          errorMsg = 'Transferencia no debe ser mayor que Autorizado';
        }
      }
    }

    return errorMsg;
  }, [localSolicitud.montoTransferencia, localSolicitud.montoAutorizado, localSolicitud.montoSolicitado, localSolicitud.producto, localSolicitud.solicitudEsCleanUp]);

  // Sincronizar el error calculado con el estado (para guardarlo)
  useEffect(() => {
    if (localSolicitud.errorTransferencia !== calculatedErrorMontos) {
      setLocalSolicitud(prev => prev ? { ...prev, errorTransferencia: calculatedErrorMontos } : prev);
    }
  }, [calculatedErrorMontos]);

  // Usar el error calculado directamente (no depender del estado que puede tener delay)
  const hasErrorMontos = !!calculatedErrorMontos;
  const displayedError = calculatedErrorMontos;

  const { isFieldAllowed } = useAuth();
  const { plazos: plazosOptions } = usePlazosDisponibles();
  const canEditInheritedID = isFieldAllowed('inheritedID');
  const canEditOwnerID = isFieldAllowed('ownerID');
  const canSeeRegion = isFieldAllowed('regionNombre');
  const canEditClienteEmpresa = isFieldAllowed('clienteEmpresa');
  const canSeeVoboRequerido = isFieldAllowed('voboRequerido');
  const canSeeClienteTipoEmpresa = isFieldAllowed('clienteTipoEmpresa');
  const canSeeClienteNivelRiesgoEmpresa = isFieldAllowed('clienteNivelRiesgoEmpresa');
  const canSeeClientePagadora = isFieldAllowed('clientePagadora');
  const canEditClienteNombre = isFieldAllowed('clienteNombre');
  const canEditClienteApellidoPaterno = isFieldAllowed('clienteApellidoPaterno');
  const canEditClienteApellidoMaterno = isFieldAllowed('clienteApellidoMaterno');
  const canEditClienteNuevoRenovacion = isFieldAllowed('clienteNuevoRenovacion');
  const canEditClientePuesto = isFieldAllowed('clientePuesto');
  const canEditClienteDirectivo = isFieldAllowed('clienteDirectivo');
  const canEditClienteSindicalizado = isFieldAllowed('clienteSindicalizado');
  const canEditClienteFechaIngreso = isFieldAllowed('clienteFechaIngreso');
  const canEditClienteAntiguedadMeses = isFieldAllowed('clienteAntiguedadMeses');
  const canEditClienteClabe = isFieldAllowed('clienteClabe');
  const canEditFrecuenciaDePago = isFieldAllowed('frecuenciaDePago');
  const canEditClienteSalario = isFieldAllowed('clienteSalario');
  const canEditClienteDeduccionesSemanales = isFieldAllowed('clienteDeduccionesSemanales');
  const canEditClienteDeduccionesQuincenales = isFieldAllowed('clienteDeduccionesQuincenales');
  const canEditClienteDeduccionesMensuales = isFieldAllowed('clienteDeduccionesMensuales');
  const canEditClienteDeduccionesPeriodo = isFieldAllowed('clienteDeduccionesPeriodo');
  const canEditProducto = isFieldAllowed('producto');
  const canEditMontoSolicitado = isFieldAllowed('montoSolicitado');
  const canEditSolicitudDestinoCredito = isFieldAllowed('solicitudDestinoCredito');
  const canEditPlazoSolicitado = isFieldAllowed('plazoSolicitado');
  const canEditPolizaSeguro = isFieldAllowed('polizaSeguro');
  const canEditSolicitudEsCleanUp = isFieldAllowed('solicitudEsCleanUp');
  const canEditSolicitudNumeroControlAnterior = isFieldAllowed('solicitudNumeroControlAnterior');
  const canEditSolicitudNumeroControl = isFieldAllowed('solicitudNumeroControl');
  const canEditSolicitudTasaTipoAnterior = isFieldAllowed('solicitudTasaTipoAnterior');
  const canEditSolicitudSaldoActualSIACAnterior = isFieldAllowed('solicitudSaldoActualSIACAnterior');
  const canEditSolicitudTotalPagadoSIACAnterior = isFieldAllowed('solicitudTotalPagadoSIACAnterior');
  const canEditSolicitudSaldoParcialidadesNumeroAnterior = isFieldAllowed('solicitudSaldoParcialidadesNumeroAnterior');
  const canEditSolicitudSaldoParcialidadesMontoAnterior = isFieldAllowed('solicitudSaldoParcialidadesMontoAnterior');
  const canEditSolicitudMontoOriginalAnterior = isFieldAllowed('solicitudMontoOriginalAnterior');
  const canEditSolicitudAvanceCreditoAnterior = isFieldAllowed('solicitudAvanceCreditoAnterior');
  const canEditSolicitudIncrementoSaldoActual = isFieldAllowed('solicitudIncrementoSaldoActual');
  const canEditSolicitudSaldoTotalAnterior = isFieldAllowed('solicitudSaldoTotalAnterior');
  const canEditTipoListaNegra = isFieldAllowed('tipoListaNegra');
  const canEditSaldo1a89 = isFieldAllowed('saldo1a89');
  const canEditSaldoMayor90 = isFieldAllowed('saldoMayor90');
  const canEditSaldoMayor120 = isFieldAllowed('saldoMayor120');
  const canEditSolicitudMop = isFieldAllowed('solicitudMop');
  const canSeeTipoBuroCredito = isFieldAllowed('tipoBuroCredito');
  const canSeeMontoBuenBuro = isFieldAllowed('montoBuenBuro');
  const canEditLineaDeCredito = isFieldAllowed('lineaDeCredito');
  const canEditFechaVencimientoLineaDeCredito = isFieldAllowed('fechaVencimientoLineaDeCredito');
  const canSeeClientePrestarHasta = isFieldAllowed('clientePrestarHasta');
  const canSeeClienteAvalRequerido = isFieldAllowed('clienteAvalRequerido');
  const canSeeMontoMaximoPolitica = isFieldAllowed('montoMaximoPolitica');
  const canSeeMontoPropuesto = isFieldAllowed('montoPropuesto');
  const canSeePlazoMaximoPolitica = isFieldAllowed('plazoMaximoPolitica');
  const canSeeTasaDeComisionPolitica = isFieldAllowed('tasaDeComisionPolitica');
  const canSeeMontoComisionPolitica = isFieldAllowed('montoComisionPolitica');
  const canSeeTasaPolitica = isFieldAllowed('tasaPolitica');
  const canEditMontoAutorizado = isFieldAllowed('montoAutorizado');
  const canEditPlazoAutorizado = isFieldAllowed('plazoAutorizado');
  const canEditTasaAutorizada = isFieldAllowed('tasaAutorizada');
  const canEditTasaDeComision = isFieldAllowed('tasaDeComision');
  const canSeeMontoComision = isFieldAllowed('montoComision');
  const canEditMontoComision = isFieldAllowed('montoComision');
  const canEditMontoTransferencia = isFieldAllowed('montoTransferencia');
  const canSeeErrorTransferencia = isFieldAllowed('errorTransferencia');
  const canSeeDocumentos = isFieldAllowed('documentos');
  const canAddArchivos = isFieldAllowed('addArchivos');
  const canListaArchivos = isFieldAllowed('listaArchivos');
  const canEditFechaAnalisisConluido = isFieldAllowed('fechaAnalisisConluido');
  const canEditFechaEmisionContrato = isFieldAllowed('fechaEmisionContrato');
  const canEditFechaPrimerCobro = isFieldAllowed('fechaPrimerCobro');
  const canSeeNumeroPagos = isFieldAllowed('numeroPagos');
  const canSeeMontoCapacidadDePago = isFieldAllowed('montoCapacidadDePago');
  const canSeePagoEstimadoPeriodo = isFieldAllowed('pagoEstimadoPeriodo');
  const canSeeCostoSeguroParcialidad = isFieldAllowed('costoSeguroParcialidad');
  const canSeePagoTotalPeriodo = isFieldAllowed('pagoTotalPeriodo');
  const canSeeCapacidadDePago = isFieldAllowed('capacidadDePago');
  const canEditExcepcionDocumentada1 = isFieldAllowed('excepcionDocumentada1');
  const canEditExcepcionDocumentada2 = isFieldAllowed('excepcionDocumentada2');
  const canEditExcepcionDocumentada3 = isFieldAllowed('excepcionDocumentada3');
  const canSeeSolicitudSeguroFinanciado = isFieldAllowed('solicitudSeguroFinanciado');
  const canSeeComentarios = isFieldAllowed('comentarios');
  const canAccionRealizarAnalisis = isFieldAllowed('accionRealizarAnalisis');

  const inputCls = "w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500";
  const readOnlyCls = "w-full px-2 py-1 border border-gray-300 rounded bg-gray-50";
  const purpleCls = "w-full px-2 py-1 border border-gray-300 rounded bg-purple-100 text-purple-800 font-medium";
  const greenCls = "w-full px-2 py-1 border border-green-400 rounded bg-green-50 focus:ring-1 focus:ring-green-500 focus:border-green-500";
  const labelCls = "px-3 py-2 text-right font-medium text-gray-600 border border-gray-200 w-1/3";
  const cellCls = "px-3 py-2 border border-gray-200";
  const headerCls = "px-3 py-2 font-bold text-gray-700 border border-gray-300";

  const ReadRow = ({ label, value }: { label: string; value: string }) => (
    <tr>
      <td className={labelCls}>{label}</td>
      <td className={cellCls}>
        <input type="text" className={readOnlyCls} value={value} readOnly />
      </td>
    </tr>
  );

  const MoneyRow = ({ label, value }: { label: string; value: number }) => (
    <tr>
      <td className={labelCls}>{label}</td>
      <td className={cellCls}>
        <input type="text" className={readOnlyCls + ' font-medium'} value={fmt(value)} readOnly />
      </td>
    </tr>
  );

  return (
    <div className="space-y-4">
      {error && (
        <div className="p-3 rounded border border-red-200 bg-red-50 text-red-700 text-sm">
          {error}
        </div>
      )}

      <table className="w-full border-collapse text-sm">
        <tbody>
          {/* ═══════════ 1. Asesor / Region ═══════════ */}
          <tr className="bg-gray-100">
            <td colSpan={2} className={headerCls}>1. Asesor / Region</td>
          </tr>
          <tr>
            <td className={labelCls}>Asesor Final</td>
            <td className={cellCls}>
              <input type="text" className={readOnlyCls} value={asesores.find(a => a.ownerID === localSolicitud.inheritedID)?.name || asesores.find(a => a.ownerID === localSolicitud.inheritedID)?.userName || localSolicitud.inheritedID || ''} readOnly />
            </td>
          </tr>
          <tr>
            <td className={labelCls}>Asesor Origen</td>
            <td className={cellCls}>
              <input type="text" className={readOnlyCls} value={(allUsers || asesores).find(a => a.ownerID === localSolicitud.ownerID)?.name || (allUsers || asesores).find(a => a.ownerID === localSolicitud.ownerID)?.userName || localSolicitud.ownerID || ''} readOnly />
            </td>
          </tr>
          {canSeeRegion && <ReadRow label="Region" value={localSolicitud.regionNombre || ''} />}

          {/* ═══════════ 2. Datos de la Empresa ═══════════ */}
          {(canEditClienteEmpresa || canSeeVoboRequerido || canSeeClienteTipoEmpresa || canSeeClienteNivelRiesgoEmpresa || canSeeClientePagadora) && (
          <>
          <tr className="bg-gray-100">
            <td colSpan={2} className={headerCls}>2. Datos de la Empresa</td>
          </tr>
          {canEditClienteEmpresa && (
          <tr>
            <td className={labelCls}>Empresa</td>
            <td className={cellCls}>
              {loadingEmpresas ? (
                <div className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-50 text-gray-500 flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Cargando empresas...
                </div>
              ) : empresas.length === 0 ? (
                <div className="w-full px-2 py-1 border border-yellow-300 rounded bg-yellow-50 text-yellow-700 text-sm">
                  No hay empresas asignadas a este asesor
                </div>
              ) : (
                <select className={inputCls} value={localSolicitud.clienteEmpresa || ''} onChange={(e) => {
                  const nombre = e.target.value;
                  const emp = empresas.find(em => em.nombre === nombre);
                  const tipoEmp = emp?.tipo && (emp.tipo === 'Interna' || emp.tipo === 'Externa') ? emp.tipo : 'Externa';
                  const nivelEmp = emp?.nivel || 'Riesgo 1';
                  setLocalSolicitud(prev => prev ? {
                    ...prev,
                    clienteEmpresa: nombre,
                    regionNombre: emp?.region || '',
                    clienteTipoEmpresa: nombre ? tipoEmp : '',
                    clienteNivelRiesgoEmpresa: nombre ? nivelEmp : '',
                    voboRequerido: emp?.voboRequerido || '',
                    clientePagadora: emp?.empresaPagadora || '',
                  } : prev);
                  setHasChanges(true);
                }}>
                  <option value="">Seleccionar empresa...</option>
                  {empresas.map(emp => <option key={emp.id || emp.nombre} value={emp.nombre}>{emp.nombre}</option>)}
                </select>
              )}
            </td>
          </tr>
          )}
          {canSeeVoboRequerido && (
          <tr>
            <td className={labelCls}>Se Requiere VoBo</td>
            <td className={cellCls}><input type="text" className={purpleCls} value={localSolicitud.voboRequerido || ''} readOnly /></td>
          </tr>
          )}
          {canSeeClienteTipoEmpresa && <ReadRow label="Tipo de Empresa" value={localSolicitud.clienteTipoEmpresa || ''} />}
          {canSeeClienteNivelRiesgoEmpresa && <ReadRow label="Nivel Riesgo Empresa" value={localSolicitud.clienteNivelRiesgoEmpresa || ''} />}
          {canSeeClientePagadora && (
          <tr>
            <td className={labelCls}>Pagadora</td>
            <td className={cellCls}><input type="text" className={purpleCls} value={localSolicitud.clientePagadora || ''} readOnly /></td>
          </tr>
          )}
          </>
          )}

          {/* ═══════════ 3. Datos del Cliente ═══════════ */}
          <tr className="bg-gray-100">
            <td colSpan={2} className={headerCls}>3. Datos del Cliente</td>
          </tr>
          {canEditClienteNombre && (
          <tr>
            <td className={labelCls}>Nombre</td>
            <td className={cellCls}><input type="text" className={inputCls} value={localSolicitud.clienteNombre || ''} onChange={(e) => update('clienteNombre', e.target.value)} /></td>
          </tr>
          )}
          {canEditClienteApellidoPaterno && (
          <tr>
            <td className={labelCls}>Apellido Paterno</td>
            <td className={cellCls}><input type="text" className={inputCls} value={localSolicitud.clienteApellidoPaterno || ''} onChange={(e) => update('clienteApellidoPaterno', e.target.value)} /></td>
          </tr>
          )}
          {canEditClienteApellidoMaterno && (
          <tr>
            <td className={labelCls}>Apellido Materno</td>
            <td className={cellCls}><input type="text" className={inputCls} value={localSolicitud.clienteApellidoMaterno || ''} onChange={(e) => update('clienteApellidoMaterno', e.target.value)} /></td>
          </tr>
          )}
          {canEditClienteNuevoRenovacion && (
          <tr>
            <td className={labelCls}>Nuevo / Renovacion</td>
            <td className={cellCls}>
              <select className={inputCls} value={localSolicitud.clienteNuevoRenovacion || ''} onChange={(e) => update('clienteNuevoRenovacion', e.target.value)}>
                <option value="">Seleccionar...</option>
                <option value="Nuevo">Nuevo</option>
                <option value="Renovación">Renovacion</option>
              </select>
            </td>
          </tr>
          )}
          {canEditClientePuesto && (
          <tr>
            <td className={labelCls}>Puesto</td>
            <td className={cellCls}><input type="text" className={inputCls} value={localSolicitud.clientePuesto || ''} onChange={(e) => update('clientePuesto', e.target.value)} /></td>
          </tr>
          )}
          {canEditClienteDirectivo && (
          <tr>
            <td className={labelCls}>Es Director</td>
            <td className={cellCls}>
              <select className={inputCls} value={localSolicitud.clienteDirectivo === true ? 'Si' : localSolicitud.clienteDirectivo === false ? 'No' : ''} onChange={(e) => update('clienteDirectivo', e.target.value === 'Si' ? true : e.target.value === 'No' ? false : undefined)}>
                <option value="">Seleccionar...</option>
                <option value="Si">Si</option>
                <option value="No">No</option>
              </select>
            </td>
          </tr>
          )}
          {canEditClienteSindicalizado && (
          <tr>
            <td className={labelCls}>Sindicalizado</td>
            <td className={cellCls}>
              <select className={inputCls} value={localSolicitud.clienteSindicalizado || ''} onChange={(e) => update('clienteSindicalizado', e.target.value)}>
                <option value="">Seleccionar...</option>
                <option value="No Aplica">No Aplica</option>
                <option value="Auxiliar Caabsa Eagle">Auxiliar Caabsa Eagle</option>
                <option value="Chofer Caabsa Eagle">Chofer Caabsa Eagle</option>
              </select>
            </td>
          </tr>
          )}
          {canEditClienteFechaIngreso && (
          <tr>
            <td className={labelCls}>Fecha de Ingreso</td>
            <td className={cellCls}><input type="date" className={inputCls} value={String(localSolicitud.clienteFechaIngreso || '').split('T')[0].split(' ')[0]} onChange={(e) => update('clienteFechaIngreso', e.target.value)} /></td>
          </tr>
          )}
          {canEditClienteAntiguedadMeses && (
          <tr>
            <td className={labelCls}>Antiguedad (meses)</td>
            <td className={cellCls}><input type="number" className={inputCls} value={localSolicitud.clienteAntiguedadMeses ?? ''} onChange={(e) => update('clienteAntiguedadMeses', e.target.value ? Number(e.target.value) : undefined)} /></td>
          </tr>
          )}
          {canEditClienteClabe && (
          <tr>
            <td className={labelCls}>Cuenta Clabe</td>
            <td className={cellCls}><input type="text" inputMode="numeric" pattern="[0-9]*" className={inputCls} value={localSolicitud.clienteClabe || ''} onChange={(e) => update('clienteClabe', e.target.value.replace(/\D/g, ''))} placeholder="Solo números" /></td>
          </tr>
          )}

          {/* ═══════════ 4. Ingresos y Deducciones ═══════════ */}
          <tr className="bg-gray-100">
            <td colSpan={2} className={headerCls}>4. Ingresos y Deducciones</td>
          </tr>
          {canEditFrecuenciaDePago && (
          <tr>
            <td className={labelCls}>Frecuencia de Pago</td>
            <td className={cellCls}>
              <select className={inputCls} value={localSolicitud.frecuenciaDePago || ''} onChange={(e) => update('frecuenciaDePago', e.target.value)}>
                <option value="">Seleccionar...</option>
                <option value="Semanal">Semanal</option>
                <option value="Quincenal">Quincenal</option>
                <option value="Catorcenal">Catorcenal</option>
                <option value="Mensual">Mensual</option>
              </select>
            </td>
          </tr>
          )}
          {canEditClienteSalario && (
          <tr>
            <td className={labelCls}>Ingreso Bruto Mensual ($)</td>
            <td className={cellCls}><input type="number" step="0.01" className={greenCls} value={localSolicitud.clienteSalario ?? ''} onChange={(e) => update('clienteSalario', e.target.value ? Number(e.target.value) : undefined)} /></td>
          </tr>
          )}
          {canEditClienteDeduccionesSemanales && (
          <tr>
            <td className={labelCls}>Deducciones Semanales ($)</td>
            <td className={cellCls}><input type="number" step="0.01" className={inputCls} value={localSolicitud.clienteDeduccionesSemanales ?? ''} onChange={(e) => update('clienteDeduccionesSemanales', e.target.value ? Number(e.target.value) : undefined)} /></td>
          </tr>
          )}
          {canEditClienteDeduccionesQuincenales && (
          <tr>
            <td className={labelCls}>Deducciones Quincenales ($)</td>
            <td className={cellCls}><input type="number" step="0.01" className={inputCls} value={localSolicitud.clienteDeduccionesQuincenales ?? ''} onChange={(e) => update('clienteDeduccionesQuincenales', e.target.value ? Number(e.target.value) : undefined)} /></td>
          </tr>
          )}
          {canEditClienteDeduccionesMensuales && (
          <tr>
            <td className={labelCls}>Deducciones Mensuales ($)</td>
            <td className={cellCls}><input type="number" step="0.01" className={inputCls} value={localSolicitud.clienteDeduccionesMensuales ?? ''} onChange={(e) => update('clienteDeduccionesMensuales', e.target.value ? Number(e.target.value) : undefined)} /></td>
          </tr>
          )}
          {canEditClienteDeduccionesPeriodo && (
          <tr>
            <td className={labelCls}>Total Deducciones / Periodo ($)</td>
            <td className={cellCls}><input type="number" step="0.01" className={inputCls} value={localSolicitud.clienteDeduccionesPeriodo ?? ''} onChange={(e) => update('clienteDeduccionesPeriodo', e.target.value ? Number(e.target.value) : undefined)} /></td>
          </tr>
          )}

          {/* ═══════════ 5. Datos de la Solicitud y Seguro ═══════════ */}
          <tr className="bg-gray-100">
            <td colSpan={2} className={headerCls}>5. Datos de la Solicitud y Seguro</td>
          </tr>
          {canEditProducto && (
          <tr>
            <td className={labelCls}>Producto</td>
            <td className={cellCls}>
              <select className={inputCls} value={localSolicitud.producto || ''} onChange={(e) => update('producto', e.target.value)}>
                <option value="">Seleccionar...</option>
                <option value="Nómina">Nomina</option>
                <option value="Adelanto de Nómina">Adelanto de Nomina</option>
                <option value="Enganche Hipotecario">Enganche Hipotecario</option>
              </select>
            </td>
          </tr>
          )}
          {canEditMontoSolicitado && (
          <tr>
            <td className={labelCls}>Monto Solicitado ($)</td>
            <td className={cellCls}><MontoInput className={inputCls} value={localSolicitud.montoSolicitado} onChange={(n) => update('montoSolicitado', n)} /></td>
          </tr>
          )}
          {canEditSolicitudDestinoCredito && (
          <tr>
            <td className={labelCls}>Destino del Credito</td>
            <td className={cellCls}><input type="text" className={inputCls} value={localSolicitud.solicitudDestinoCredito || ''} onChange={(e) => update('solicitudDestinoCredito', e.target.value)} /></td>
          </tr>
          )}
          {canEditPlazoSolicitado && (
          <tr>
            <td className={labelCls}>Plazo Solicitado (meses)</td>
            <td className={cellCls}>
              <select className={inputCls} value={localSolicitud.plazoSolicitado || ''} onChange={(e) => update('plazoSolicitado', e.target.value)}>
                <option value="">Seleccionar...</option>
                {plazosOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </td>
          </tr>
          )}
          {canEditPolizaSeguro && localSolicitud.producto !== 'Adelanto de Nómina' && (
          <tr>
            <td className={labelCls}>Poliza de Seguro</td>
            <td className={cellCls}>
              <select className={inputCls} value={localSolicitud.polizaSeguro || ''} onChange={(e) => update('polizaSeguro', e.target.value)}>
                <option value="">Seleccionar...</option>
                <option value="Ninguna">Ninguna</option>
                <option value="25000">$25,000</option>
                <option value="50000">$50,000</option>
                <option value="75000">$75,000</option>
                <option value="100000">$100,000</option>
              </select>
            </td>
          </tr>
          )}

          {/* ═══════════ 6. Quien es Quien (CN y AN-001) ═══════════ */}
          <tr className="bg-gray-100">
            <td colSpan={2} className={headerCls}>6. Quien es Quien (CN y AN-001)</td>
          </tr>
          {canEditTipoListaNegra && (
          <tr>
            <td className={labelCls}>Listas Negras Tipo</td>
            <td className={cellCls}>
              <select className={inputCls} value={localSolicitud.tipoListaNegra || ''} onChange={(e) => update('tipoListaNegra', e.target.value)}>
                <option value="">Seleccionar...</option>
                <option value="No Aplica">No Aplica</option>
                <option value="Sin Resultados">Sin Resultados</option>
                <option value="Es PEP">Es PEP</option>
                <option value="Persona Bloqueada">Persona Bloqueada</option>
                <option value="69 o 69 Bis">69 o 69 Bis</option>
                <option value="Listas Negras Internacionales">Listas Negras Internacionales</option>
                <option value="Es Proceso Juridico">Es Proceso Juridico</option>
              </select>
            </td>
          </tr>
          )}

          {/* ═══════════ 7. Buro de Credito (Solo si es CN) ═══════════ */}
          <tr className={localSolicitud.producto === 'Adelanto de Nómina' ? 'bg-gray-50' : 'bg-gray-100'}>
            <td colSpan={2} className={localSolicitud.producto === 'Adelanto de Nómina' ? 'px-3 py-2 font-semibold text-gray-300 border border-gray-200' : headerCls}>7. Buro de Credito (Solo si es CN)</td>
          </tr>
          {localSolicitud.producto !== 'Adelanto de Nómina' && (<>
          {canEditSaldo1a89 && (
          <tr>
            <td className={labelCls}>Saldo de 1-89 dias ($)</td>
            <td className={cellCls}><input type="number" step="0.01" className={inputCls} value={localSolicitud.saldo1a89 ?? ''} onChange={(e) => update('saldo1a89', e.target.value ? Number(e.target.value) : undefined)} /></td>
          </tr>
          )}
          {canEditSaldoMayor90 && (
          <tr>
            <td className={labelCls}>Saldo con 90-120 dias ($)</td>
            <td className={cellCls}><input type="number" step="0.01" className={inputCls} value={localSolicitud.saldoMayor90 ?? ''} onChange={(e) => update('saldoMayor90', e.target.value ? Number(e.target.value) : undefined)} /></td>
          </tr>
          )}
          {canEditSaldoMayor120 && (
          <tr>
            <td className={labelCls}>Saldo mayor a 120 dias ($)</td>
            <td className={cellCls}><input type="number" step="0.01" className={inputCls} value={localSolicitud.saldoMayor120 ?? ''} onChange={(e) => update('saldoMayor120', e.target.value ? Number(e.target.value) : undefined)} /></td>
          </tr>
          )}
          {canEditSolicitudMop && (
          <tr>
            <td className={labelCls}>MOP mas alto</td>
            <td className={cellCls}>
              <select className={inputCls} value={localSolicitud.solicitudMop || ''} onChange={(e) => update('solicitudMop', e.target.value)}>
                <option value="">Seleccionar...</option>
                {['00','01','02','03','04','05','06','07','96','97'].map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </td>
          </tr>
          )}
          </>)}

          {/* ═══════════ 8. Clean Up (Solo si es CN) ═══════════ */}
          <tr className={localSolicitud.producto === 'Adelanto de Nómina' ? 'bg-gray-50' : 'bg-gray-100'}>
            <td colSpan={2} className={localSolicitud.producto === 'Adelanto de Nómina' ? 'px-3 py-2 font-semibold text-gray-300 border border-gray-200' : headerCls}>8. Clean Up (Solo si es CN)</td>
          </tr>
          {localSolicitud.producto !== 'Adelanto de Nómina' && (<>
          {canEditSolicitudEsCleanUp && (
          <tr>
            <td className={labelCls}>Es Clean Up</td>
            <td className={cellCls}>
              <select className={greenCls} value={localSolicitud.solicitudEsCleanUp === true ? 'Sí' : localSolicitud.solicitudEsCleanUp === false ? 'No' : ''} onChange={(e) => update('solicitudEsCleanUp', e.target.value === 'Sí' ? true : e.target.value === 'No' ? false : undefined)}>
                <option value="">Seleccionar...</option>
                <option value="Sí">Sí</option>
                <option value="No">No</option>
              </select>
            </td>
          </tr>
          )}
          {canEditSolicitudNumeroControlAnterior && (
          <tr>
            <td className={labelCls}>Numero de Control Anterior</td>
            <td className={cellCls}><input type="text" className={greenCls} value={localSolicitud.solicitudNumeroControlAnterior || ''} onChange={(e) => update('solicitudNumeroControlAnterior', e.target.value)} /></td>
          </tr>
          )}
          {canEditSolicitudTasaTipoAnterior && (
          <tr>
            <td className={labelCls}>Tipo de Tasa de Credito Anterior</td>
            <td className={cellCls}>
              <select className={greenCls} value={localSolicitud.solicitudTasaTipoAnterior || 'Tasa Global'} onChange={(e) => update('solicitudTasaTipoAnterior', e.target.value)}>
                <option value="Tasa Global">Tasa Global</option>
                <option value="Tasa sobre Saldos Insolutos">Tasa sobre Saldos Insolutos</option>
              </select>
            </td>
          </tr>
          )}
          {canEditSolicitudSaldoActualSIACAnterior && (
          <tr>
            <td className={labelCls}>Saldo Actual Cdto Anterior SIAC ($)</td>
            <td className={cellCls}><input type="number" step="0.01" className={greenCls} value={localSolicitud.solicitudSaldoActualSIACAnterior ?? ''} onChange={(e) => update('solicitudSaldoActualSIACAnterior', e.target.value ? Number(e.target.value) : undefined)} /></td>
          </tr>
          )}
          {canEditSolicitudTotalPagadoSIACAnterior && (
          <tr>
            <td className={labelCls}>Total Pagado Cdto Anterior SIAC ($)</td>
            <td className={cellCls}><input type="number" step="0.01" className={greenCls} value={localSolicitud.solicitudTotalPagadoSIACAnterior ?? ''} onChange={(e) => update('solicitudTotalPagadoSIACAnterior', e.target.value ? Number(e.target.value) : undefined)} /></td>
          </tr>
          )}
          {canEditSolicitudSaldoParcialidadesNumeroAnterior && (
          <tr>
            <td className={labelCls}>Parcialidades Por Cobrar SIAC (#)</td>
            <td className={cellCls}><input type="number" step="0.01" className={greenCls} value={localSolicitud.solicitudSaldoParcialidadesNumeroAnterior ?? ''} onChange={(e) => update('solicitudSaldoParcialidadesNumeroAnterior', e.target.value ? Number(e.target.value) : undefined)} /></td>
          </tr>
          )}
          {canEditSolicitudSaldoParcialidadesMontoAnterior && (
          <tr>
            <td className={labelCls}>Parcialidades Por Cobrar SIAC ($)</td>
            <td className={cellCls}><input type="number" step="0.01" className={greenCls} value={localSolicitud.solicitudSaldoParcialidadesMontoAnterior ?? ''} onChange={(e) => update('solicitudSaldoParcialidadesMontoAnterior', e.target.value ? Number(e.target.value) : undefined)} /></td>
          </tr>
          )}
          {canEditSolicitudMontoOriginalAnterior && <MoneyRow label="Total Cdto Anterior ($)" value={localSolicitud.solicitudMontoOriginalAnterior ?? 0} />}
          {canEditSolicitudAvanceCreditoAnterior && <ReadRow label="Pagado Cdto Anterior (%)" value={localSolicitud.solicitudAvanceCreditoAnterior != null ? `${localSolicitud.solicitudAvanceCreditoAnterior}%` : ''} />}
          {canEditSolicitudIncrementoSaldoActual && <ReadRow label="Incremento al Saldo Actual (%)" value={localSolicitud.solicitudIncrementoSaldoActual != null ? `${localSolicitud.solicitudIncrementoSaldoActual}%` : ''} />}
          {canEditSolicitudSaldoTotalAnterior && <MoneyRow label="Total Por Cobrar Cdto Anterior ($)" value={localSolicitud.solicitudSaldoTotalAnterior ?? 0} />}
          </>)}{/* fin sección 8 */}

          {/* ═══════════ 9. Linea de Credito (Solo si es AN) ═══════════ */}
          {(localSolicitud.producto || '').toLowerCase().includes('adelanto') && (canEditLineaDeCredito || canEditFechaVencimientoLineaDeCredito) && (
            <>
              <tr className="bg-gray-100">
                <td colSpan={2} className={headerCls}>9. Linea de Credito (Solo si es AN)</td>
              </tr>
              {canEditLineaDeCredito && (
              <tr>
                <td className={labelCls}>Línea de Crédito ($)</td>
                <td className={cellCls}>
                  <input type="text" inputMode="decimal" maxLength={16} className={greenCls} value={localSolicitud.lineaDeCredito ?? localSolicitud.montoAutorizado ?? ''} onInput={(e) => { e.currentTarget.value = e.currentTarget.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1'); }} onChange={(e) => { const n = parseFloat(e.target.value) || 0; if (n <= 9999999999999) update('lineaDeCredito', n || undefined); }} />
                </td>
              </tr>
              )}
              {canEditFechaVencimientoLineaDeCredito && (
              <tr>
                <td className={labelCls}>Vencimiento de Linea de Cto</td>
                <td className={cellCls}><input type="date" className={greenCls} value={String(localSolicitud.fechaVencimientoLineaDeCredito || '').split('T')[0].split(' ')[0]} onChange={(e) => update('fechaVencimientoLineaDeCredito', e.target.value)} /></td>
              </tr>
              )}
            </>
          )}

          {/* ═══════════ 10. Propuesta Aplicando Politicas ═══════════ */}
          <tr className="bg-blue-50">
            <td colSpan={2} className="px-3 py-2 font-bold text-blue-800 border border-blue-200">10. Propuesta Aplicando Politicas</td>
          </tr>
          {canSeeClientePrestarHasta && <ReadRow label="Prestar hasta (meses)" value={localSolicitud.clientePrestarHasta || ''} />}
          {canSeeClienteAvalRequerido && <ReadRow label="Aval Requerido" value={localSolicitud.clienteAvalRequerido || ''} />}
          {canSeeMontoMaximoPolitica && <MoneyRow label="Monto Maximo Politica ($)" value={localSolicitud.montoMaximoPolitica ?? 0} />}
          {canSeeMontoPropuesto && <MoneyRow label="Monto Propuesto ($)" value={localSolicitud.montoPropuesto ?? 0} />}
          {canSeePlazoMaximoPolitica && <ReadRow label="Plazo Max Politica (meses)" value={localSolicitud.plazoMaximoPolitica || ''} />}
          {canSeeTipoBuroCredito && <ReadRow label="Buro de Credito" value={localSolicitud.tipoBuroCredito || ''} />}
          {canSeeMontoBuenBuro && <MoneyRow label="Oferta Buen Buro ($)" value={localSolicitud.montoBuenBuro ?? 0} />}
          {canSeeTasaDeComisionPolitica && <ReadRow label="Tasa Comision Politica (%)" value={localSolicitud.tasaDeComisionPolitica != null ? `${localSolicitud.tasaDeComisionPolitica}%` : ''} />}
          {canSeeMontoComisionPolitica && <MoneyRow label="Monto Comision Politica ($)" value={localSolicitud.montoComisionPolitica ?? 0} />}
          {canSeeTasaPolitica && <ReadRow label="Tasa por Politica (%)" value={localSolicitud.tasaPolitica != null ? `${localSolicitud.tasaPolitica}%` : ''} />}

          {/* ═══════════ 11. Excepciones Documentadas ═══════════ */}
          {(canEditExcepcionDocumentada1 || canEditExcepcionDocumentada2 || canEditExcepcionDocumentada3) && (
          <tr className="bg-gray-100">
            <td colSpan={2} className={headerCls}>11. Excepciones Documentadas</td>
          </tr>
          )}
          {canEditExcepcionDocumentada1 && (
          <tr>
            <td className={labelCls}>1ra Excepcion documentada por</td>
            <td className={cellCls}>
              <select className={greenCls} value={localSolicitud.excepcionDocumentada1 || ''} onChange={(e) => update('excepcionDocumentada1', e.target.value)}>
                {EXCEPCION_OPTIONS.map(v => <option key={v} value={v}>{v || 'Seleccionar...'}</option>)}
              </select>
            </td>
          </tr>
          )}
          {canEditExcepcionDocumentada2 && (
          <tr>
            <td className={labelCls}>2da Excepcion documentada por</td>
            <td className={cellCls}>
              <select className={greenCls} value={localSolicitud.excepcionDocumentada2 || ''} onChange={(e) => update('excepcionDocumentada2', e.target.value)}>
                {EXCEPCION_OPTIONS.map(v => <option key={v} value={v}>{v || 'Seleccionar...'}</option>)}
              </select>
            </td>
          </tr>
          )}
          {canEditExcepcionDocumentada3 && (
          <tr>
            <td className={labelCls}>3er Excepcion documentada por</td>
            <td className={cellCls}>
              <select className={greenCls} value={localSolicitud.excepcionDocumentada3 || ''} onChange={(e) => update('excepcionDocumentada3', e.target.value)}>
                {EXCEPCION_OPTIONS.map(v => <option key={v} value={v}>{v || 'Seleccionar...'}</option>)}
              </select>
            </td>
          </tr>
          )}

          {/* ═══════════ 12. Autorizacion ═══════════ */}
          <tr className="bg-green-50">
            <td colSpan={2} className="px-3 py-2 font-bold text-green-800 border border-green-200">12. Autorizacion</td>
          </tr>
          {canEditPlazoAutorizado && (
          <tr>
            <td className={labelCls}>Plazo Autorizado (meses)</td>
            <td className={cellCls}>
              <select className={greenCls} value={localSolicitud.plazoAutorizado || ''} onChange={(e) => update('plazoAutorizado', e.target.value)}>
                <option value="">Seleccionar...</option>
                {plazosOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </td>
          </tr>
          )}
          {canEditMontoAutorizado && (
          <tr>
            <td className={labelCls}>Monto Autorizado ($)</td>
            <td className={cellCls}><MontoInput className={greenCls} value={localSolicitud.montoAutorizado} onChange={(n) => update('montoAutorizado', n)} /></td>
          </tr>
          )}
          {canEditMontoTransferencia && (
          <tr>
            <td className={labelCls}>Monto Transferencia ($)</td>
            <td className={cellCls}><MontoInput className={greenCls} value={Math.max(0, localSolicitud.montoTransferencia ?? 0)} onChange={(n) => update('montoTransferencia', n)} /></td>
          </tr>
          )}
          {canEditTasaDeComision && (
          <tr>
            <td className={labelCls}>Tasa Comision Autorizada (%)</td>
            <td className={cellCls}><input type="number" step="0.01" className={greenCls} value={localSolicitud.tasaDeComision ?? ''} onChange={(e) => update('tasaDeComision', e.target.value ? parseFloat(e.target.value) : undefined)} /></td>
          </tr>
          )}
          {canEditTasaAutorizada && (
          <tr>
            <td className={labelCls}>Tasa Autorizada (%)</td>
            <td className={cellCls}><input type="number" step="0.01" className={greenCls} value={localSolicitud.tasaAutorizada ?? ''} onChange={(e) => update('tasaAutorizada', e.target.value ? parseFloat(e.target.value) : undefined)} /></td>
          </tr>
          )}

          {/* ═══════════ 13. Condiciones del Contrato ═══════════ */}
          <tr className="bg-gray-100">
            <td colSpan={2} className={headerCls}>13. Condiciones del Contrato</td>
          </tr>
          {canEditSolicitudNumeroControl && (
          <tr>
            <td className={labelCls}>Numero de Control</td>
            <td className={cellCls}><input type="text" className={greenCls} value={localSolicitud.solicitudNumeroControl || ''} onChange={(e) => update('solicitudNumeroControl', e.target.value)} /></td>
          </tr>
          )}
          {canEditFechaEmisionContrato && (
          <tr>
            <td className={labelCls}>Fecha de Emision de Contrato</td>
            <td className={cellCls}><input type="date" className={greenCls} value={String(localSolicitud.fechaEmisionContrato || '').split('T')[0].split(' ')[0]} onChange={(e) => update('fechaEmisionContrato', e.target.value)} /></td>
          </tr>
          )}
          {canEditFechaPrimerCobro && (
          <tr>
            <td className={labelCls}>Fecha de Primer Cobro</td>
            <td className={cellCls}><input type="date" className={greenCls} value={String(localSolicitud.fechaPrimerCobro || '').split('T')[0].split(' ')[0]} onChange={(e) => update('fechaPrimerCobro', e.target.value)} /></td>
          </tr>
          )}

          {/* ═══════════ 14. Condiciones de Otorgamiento ═══════════ */}
          <tr className="bg-gray-100">
            <td colSpan={2} className={headerCls}>14. Condiciones de Otorgamiento</td>
          </tr>
          {canSeeErrorTransferencia && (
          <tr>
            <td className={labelCls}>Error por Montos</td>
            <td className={cellCls}>
              <input type="text" className={hasErrorMontos ? "w-full px-2 py-1 border border-red-400 rounded bg-red-50 text-red-700 font-medium" : readOnlyCls} value={displayedError} readOnly />
            </td>
          </tr>
          )}
          {canSeeMontoComision && (
          <tr>
            <td className={labelCls}>Monto Comision Autorizada ($)</td>
            <td className={cellCls}>
              {canEditMontoComision ? (
                <MontoInput className={greenCls} value={localSolicitud.montoComision} onChange={(n) => update('montoComision', n)} />
              ) : (
                <span className={readOnlyCls}>{fmt(localSolicitud.montoComision ?? 0)}</span>
              )}
            </td>
          </tr>
          )}
          {canSeeSolicitudSeguroFinanciado && <MoneyRow label="Seguro Financiado ($)" value={localSolicitud.solicitudSeguroFinanciado ?? 0} />}
          {canSeeNumeroPagos && (
          <tr>
            <td className={labelCls}>Numero de Pagos</td>
            <td className={cellCls}>
              <input type="text" className={readOnlyCls} value={String(localSolicitud.numeroPagos ?? '')} readOnly />
            </td>
          </tr>
          )}
          {canSeeMontoCapacidadDePago && <MoneyRow label="Capacidad de Pago ($)" value={localSolicitud.montoCapacidadDePago ?? 0} />}
          {canSeePagoEstimadoPeriodo && <MoneyRow label="Pago Estimado Crédito ($)" value={localSolicitud.pagoEstimadoPeriodo ?? 0} />}
          {canSeeCostoSeguroParcialidad && <MoneyRow label="Parcialidad Seguro ($)" value={localSolicitud.costoSeguroParcialidad ?? 0} />}
          {canSeePagoTotalPeriodo && <MoneyRow label="Pago Total Estimado ($)" value={localSolicitud.pagoTotalPeriodo ?? 0} />}
          {canSeeCapacidadDePago && (
          <tr>
            <td className={labelCls}>Capacidad de Pago</td>
            <td className={cellCls}>
              <input type="text" className={readOnlyCls + ' font-medium'} value={localSolicitud.capacidadDePago ?? ''} readOnly />
            </td>
          </tr>
          )}
        </tbody>
      </table>

      {/* Botón Realizar Análisis - Guarda datos del formulario y ejecuta cálculo de análisis de créditos */}
      {canAccionRealizarAnalisis && (
      <div className="flex justify-center py-3">
        <button
          type="button"
          className="px-6 py-2 bg-blue-600 text-white font-medium rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          onClick={async () => {
            await handleSaveForm();
            await handleRealizarAnalisisCreditos();
          }}
          disabled={isSaving || isRunningAnalisis}
        >
          {isRunningAnalisis
            ? 'Ejecutando análisis...'
            : isSaving
              ? 'Guardando...'
              : 'Realizar Análisis'}
        </button>
      </div>
      )}

      {/* Switch Análisis Concluido */}
      <table className="w-full border-collapse text-sm">
        <tbody>
          {canEditFechaAnalisisConluido && (
          <tr>
            <td className={labelCls}>Análisis Concluido</td>
            <td className={cellCls}>
              <input type="text" className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-50" value={localSolicitud.fechaAnalisisConluido || ''} readOnly />
            </td>
            <td className="px-1 py-2 border border-gray-200 w-16 text-center">
              <Switch checked={!!localSolicitud.fechaAnalisisConluido} onChange={(checked) => handleAnalisisConcluido(checked)} color="blue" disabled={isTogglingConcluido || hasErrorMontos} />
            </td>
          </tr>
          )}
          {canSeeErrorTransferencia && hasErrorMontos && (
            <tr>
              <td colSpan={3} className="px-3 py-2 text-center text-red-600 text-xs font-medium border border-red-200 bg-red-50">
                No se puede concluir el análisis: {displayedError}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {successMsg && (
        <div className="p-3 rounded border border-green-200 bg-green-50 text-green-700 text-sm font-medium">
          {successMsg}
        </div>
      )}

      {/* Observaciones y Documentos */}
      <table className="w-full border-collapse text-sm">
        <tbody>
          <tr className="bg-gray-100">
            <td colSpan={2} className={headerCls}>Observaciones y Documentos</td>
          </tr>
          {canSeeDocumentos && (
          <tr>
            <td className={labelCls}>Link Documentos</td>
            <td className={cellCls}>
              <div className="flex gap-2">
                {canAddArchivos ? (
                  <input type="text" className={inputCls + ' flex-1'} value={localSolicitud.documentos || ''} onChange={(e) => update('documentos', e.target.value)} placeholder="https://drive.google.com/..." />
                ) : (
                  <input type="text" className={readOnlyCls + ' flex-1'} value={localSolicitud.documentos || ''} readOnly />
                )}
                {canListaArchivos && localSolicitud.documentos && (
                  <button type="button" className="px-3 py-1 border border-gray-400 rounded bg-gray-100 hover:bg-gray-200 text-xs whitespace-nowrap" onClick={() => window.open(localSolicitud.documentos, '_blank')}>Ver</button>
                )}
              </div>
            </td>
          </tr>
          )}
          {canSeeComentarios && (
          <tr>
            <td className={labelCls}>Comentarios</td>
            <td className={cellCls}><textarea className={inputCls} rows={3} value={localSolicitud.comentarios || ''} onChange={(e) => update('comentarios', e.target.value)} /></td>
          </tr>
          )}
        </tbody>
      </table>

      {/* ═══════════ 15. Simulador de Condiciones de Contrato SIAC ═══════════ */}
      <SimuladorSIAC
        solicitud={localSolicitud}
        onSimular={handleSimularSIAC}
        isSimulating={isRunningAnalisis}
      />
    </div>
  );
}
